import { makeStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles';

const ACCENT_ORANGE = '#FFB55A';

const useStyles = makeStyles(theme => ({
  skeleton: {
    marginTop: theme.spacing(2),
  },
  noData: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    padding: theme.spacing(4),
    textAlign: 'center',
    width: '100%',
  },
  tableWrapper: ({ refetching }) =>
    refetching
      ? {
          opacity: 0.35,
          pointerEvents: 'none',
          position: 'relative',
        }
      : {
          background: theme.palette.common.white,
          borderRadius: theme.shape.borderRadiusLarge,
          boxShadow: '0 10px 28px rgba(16, 24, 40, 0.06)',
          overflow: 'hidden',
        },
  loader: {
    position: 'absolute',
    top: '50%',
    left: 'calc(50% - 30px)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: theme.palette.common.white,
    '@media screen and (max-width: 55em)': {
      width: '100%',
    },
  },
  thead: {
    background: theme.palette.primary[50],
    position: ({ fixedHeader, isPivoted }) => (fixedHeader || isPivoted ? 'relative' : 'sticky'),
    zIndex: '1',
    top: '-1px',

    '&:after': {
      content: '""',
      display: ({ isPivoted }) => (isPivoted ? 'none' : 'block'),
      position: 'absolute',
      width: '100%',
      height: '2px',
      background: theme.palette.secondary.main,
      bottom: 0,
    },
  },
  th: {
    background: theme.palette.primary[50],
    textAlign: 'left',
    fontWeight: 700,
    fontSize: '.85rem',
    padding: theme.spacing(2, 1),
    cursor: 'pointer',
    userSelect: 'none',
    letterSpacing: 0,
    position: 'relative',
    verticalAlign: 'middle',
    minHeight: 48,
    boxSizing: 'border-box',
    color: theme.palette.text.primary,
  },
  filterTh: {
    width: '41px',
  },
  filterHeaderCell: {
    background: theme.palette.primary[50],
    textAlign: 'right',
    '& > *': {
      display: 'inline-flex',
    },
  },
  addonHeaderCell: {
    background: theme.palette.primary[50],
    cursor: 'auto',
    textAlign: 'center',
    borderTopRightRadius: theme.shape.borderRadiusLarge,
  },
  sort: {
    fontWeight: 800,
    letterSpacing: 0,
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: '-9px',
      top: '50%',
      width: 5,
      height: 5,
      borderRadius: '100%',
      transform: 'translateY(-50%)',
      background: ACCENT_ORANGE,
    },
  },
  thContent: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 24,
  },
  sortIconWrapper: {
    flexShrink: '0',
    flexGrow: '0',
    maxWidth: '16px',
    maxHeight: '16px',
    background: theme.palette.secondary[50],
    borderRadius: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(0.5),
  },
  sortIcon: {
    width: '16px',
    height: '16px',
  },
  additionalInfo: {
    marginLeft: theme.spacing(1),
    fontWeight: 400,
    color: theme.palette.text.secondary,
    fontSize: '.875rem',
  },
  tr: {
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shorter,
    }),
  },
  focusableTd: {
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '-2px',
      background: theme.palette.secondary[50],
    },
  },
  pivotedTd: {
    borderBottom: `thin solid ${theme.palette.secondary[100]}`,
    padding: 0,
    position: 'relative',
  },
  pivotedRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gridGap: theme.spacing(1, 2),
    margin: 0,
    padding: theme.spacing(3, 1),
    alignItems: 'center',
    position: 'relative',
  },
  pivotedItem: {
    display: 'contents',
  },
  pivotedLabel: {
    fontWeight: 600,
    textAlign: 'right',
    color: theme.palette.text.secondary,
  },
  pivotedValue: {
    margin: 0,
    minWidth: 0,
  },
  pivotedValueAction: {
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '2px',
      background: theme.palette.secondary[50],
    },
  },
  addonWrapper: {
    position: 'absolute',
    top: '10px',
    right: 0,
    zIndex: 1,
  },
  clickableTr: {
    transition: '.1s',
    cursor: 'pointer',
    '@media (hover: hover)': {
      '&:hover': {
        background: theme.palette.secondary[50],
      },
      '&:active': {
        background: theme.palette.secondary[100],
      },
    },
  },
  td: {
    borderBottom: `thin solid ${theme.palette.secondary[100]}`,
    padding: theme.spacing(1.25, 1),
    wordWrap: 'break-word',
    zIndex: '-1',
    verticalAlign: 'middle',
    boxSizing: 'border-box',

    '& p': {
      margin: theme.spacing(0.5, 0),
    },
  },
  addonTd: {
    '@media screen and (max-width: 55em)': {
      position: 'absolute !important',
      top: '10px',
      right: 0,
      zIndex: 1,
    },
  },
  filtersButton: {
    padding: theme.spacing(1),
    position: 'sticky',
    top: 'calc(100% - 80px)',
    zIndex: 3,
    minHeight: 56,
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    boxSizing: 'border-box',
    background: theme.palette.primary[50],
    borderBottom: `2px solid ${theme.palette.secondary.main}`,
  },
  blockSorting: {
    cursor: 'auto',
  },
  tdContent: {
    minHeight: '47px',
    display: 'flex',
    alignItems: 'center',
    wordBreak: 'break-word',
    '@media screen and (max-width: 55em)': {
      minHeight: 'unset',
    },
  },
  rowWrapper: {
    display: 'contents',
    color: theme.palette.text.primary,
  },
  searchBar: {
    background: '',
  },
  thSearch: {
    zIndex: '1',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  searchRowContent: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'nowrap',
    minWidth: 0,
  },
  searchInputWrapper: {
    flex: '1 1 0',
    minWidth: 0,
    overflow: 'hidden',
  },
  searchAdornmentWrapper: {
    flexShrink: 0,
  },
  selectedRow: {
    background: theme.palette.secondary[50],
    '&:hover': {
      background: theme.palette.secondary[100],
    },
  },
  expandedTr: {
    background: theme.palette.primary[50] ? fade(theme.palette.primary[50], 0.2) : 'rgba(238, 243, 255, 0.2)',
  },
  expandedTd: {
    background: theme.palette.primary[50] ? fade(theme.palette.primary[50], 0.2) : 'rgba(238, 243, 255, 0.2)',
    borderBottom: `thin solid ${theme.palette.secondary[100] || theme.palette.divider}`,
    boxShadow: `inset 3px 0 0 ${theme.palette.secondary.main}`,
    padding: theme.spacing(2, 2, 2, 2.5),
    verticalAlign: 'top',
  },
  expandedContent: {
    minWidth: 0,
    width: '100%',
  },
}));

export default useStyles;
